import {useState} from 'react'
import {ViewMode} from './utils/view-mode'
import {getCurrentUserLogin} from '@github-ui/client-env'

/**
 * The state values of the comments filter
 */
export interface CommentsFilterState {
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  filterText: string
  selectedAuthor?: string
}

interface UseCommentsFilterStateResult {
  filterState: CommentsFilterState
  /** Set the filter state for the current view mode. */
  setCurrentFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
}

export const defaultFilterStates = (): Record<ViewMode, CommentsFilterState> => ({
  // "conversation" / "all"
  [ViewMode.IssueComments]: {
    filterText: '',
    hideResolvedThreads: false,
    hideOutdatedThreads: false,
  },
  // "review" / "unresolved"
  [ViewMode.ThreadPreviews]: {
    filterText: '',
    hideResolvedThreads: true,
    hideOutdatedThreads: false,
  },
  // "mine"
  [ViewMode.Mine]: {
    filterText: '',
    hideResolvedThreads: false,
    hideOutdatedThreads: false,
    selectedAuthor: getCurrentUserLogin(),
  },
})

/** Tracks the state of comments filters. */
export function useCommentsFilterState(viewMode: ViewMode): UseCommentsFilterStateResult {
  // filter state for each view mode is tracked separately
  const [filterStates, setFilterStates] = useState(defaultFilterStates)

  const setViewModeFilterState = (mode: ViewMode, updatedState: React.SetStateAction<CommentsFilterState>) =>
    setFilterStates(previous => {
      const newState = typeof updatedState === 'function' ? updatedState(previous[mode]) : updatedState
      return {...previous, [mode]: newState}
    })

  const setCurrentFilterState = (filterState: React.SetStateAction<CommentsFilterState>) =>
    setViewModeFilterState(viewMode, filterState)

  return {
    filterState: filterStates[viewMode],
    setCurrentFilterState,
  } as const
}
